import uuid from 'uuid/v4';
import { ReplaceAroundStep, ReplaceStep } from '@atlaskit/editor-prosemirror/transform';
import { getDiffObjectsForParagraph } from '../../utils/diff-match-patch/utils';
/**
 * This is a generalised collection of explicit included node types which proactive AI will operate on.
 * Types not listed here will be excluded by default.
 * If you want proactive ai to look inside a node then it's type must be listed here, otherwise the node will be skipped.
 * This is to avoid unneccessary searching through nodes which shouldn't have spelling + grammar run on them.
 */
export const getIncludedProactiveAINodeTypes = ({
  schema: {
    nodes
  }
}) => new Set([nodes.blockquote, nodes.bulletList, nodes.caption, nodes.decisionItem, nodes.decisionList, nodes.expand, nodes.heading, nodes.layoutColumn, nodes.layoutSection, nodes.listItem, nodes.nestedExpand, nodes.orderedList, nodes.panel, nodes.paragraph, nodes.table, nodes.tableRow, nodes.tableCell, nodes.tableHeader, nodes.taskItem, nodes.taskList]);

/**
 * This is a refined collection of node types which will be used to determine whether proactive ai blocks will be created
 * to keep track of the state nodes which have S+G run over them.
 */
export const getProactiveAIBlockNodeTypes = ({
  schema: {
    nodes
  }
}) => new Set([nodes.decisionItem, nodes.heading, nodes.paragraph, nodes.taskItem]);
export const getDiffObjectsForBlocks = blocksWitSuggestions => {
  const blocksDiffObjectsByBlockId = new Map();
  blocksWitSuggestions.forEach(({
    block,
    suggestion
  }) => {
    blocksDiffObjectsByBlockId.set(block.id, getDiffObjectsForParagraph(block, suggestion.suggestion || suggestion.original.text));
  });
  return blocksDiffObjectsByBlockId;
};
export function findProactiveAIBlockToUpdate(tr, pluginState, newEditorState) {
  const {
    proactiveAIBlocks,
    isSpellingAndGrammarEnabled
  } = pluginState;
  const shouldIgnoreChanges = !isSpellingAndGrammarEnabled || tr.getMeta('isRemote') || tr.getMeta('replaceDocument') || tr.getMeta('suggestionAccepted') || tr.getMeta('isAiContentTransformation');
  const nodeTypes = getProactiveAIBlockNodeTypes(newEditorState);
  const includedNodeTypes = getIncludedProactiveAINodeTypes(newEditorState);
  const doc = tr.doc;
  const replaceSteps = tr.steps.filter(step => step instanceof ReplaceStep || step instanceof ReplaceAroundStep);
  if (proactiveAIBlocks) {
    let updatedProactiveAIBlock = Array.from(proactiveAIBlocks);
    replaceSteps.forEach(step => {
      const newProactiveAIBlocks = [];

      /**
       * First find new nodes that needs to be checked against
       * proactive AI prompt.
       */
      step.getMap().forEach((oldStart, oldEnd, newStart, newEnd) => {
        doc.nodesBetween(newStart, Math.min(newEnd, tr.doc.content.size), (node, pos) => {
          if (nodeTypes.has(node.type) && !newProactiveAIBlocks.find(newProactiveAIBlock => newProactiveAIBlock.node === node)) {
            const inlineNodePositions = [];
            node.descendants((node, pos) => {
              if (node.isInline && !node.isText) {
                inlineNodePositions.push(pos);
              }
            });
            newProactiveAIBlocks.push({
              id: uuid(),
              from: pos,
              to: pos + node.nodeSize,
              inlineNodeStart: pos + 1,
              inlineNodeEnd: pos + node.nodeSize - 1,
              text: node.textContent,
              nodeTypeName: node.type.name,
              /**
               * If it's remote transaction, then we don't want new blocks
               * created from that transaction to be checked for S+G,
               * because they must be checked where it's originated.
               *
               * Also when document is loaded in confluence, collab service
               *  fetches document and fires transaction to replace whole document.
               * In that case as well, we don't want to trigger S+G for whole document.
               */
              needSpellingAndGrammarCheck: !shouldIgnoreChanges,
              newBlock: true,
              inlineNodePositions,
              node
            });
            return false;
          }
          return includedNodeTypes.has(node.type);
        });
      });

      /**
       * Now find existing range of proactiveAIBlock that needs to be
       * replaced with new blocks found.
       *
       * ASSUMPTIONS:
       * 1. We have assumed here that new proactiveAIBlocks will be in
       *    continous range.
       *    For example: New 4 blocks will be from positions 350 to 550.
       *                 Let's say there are 3 blocks before 350 and
       *                 2 blocks after 550. And there were 2 blocks in 350 to 550 before.
       *                 That means existing Array is
       *                 [b1, b2, b3, -- b4, b5 --, b6, b7 )], affected blocks are b4, b5.
       *                 So they will be replaced with new 3 blocks.
       *                 Final array will look like
       *                 [old_b1, old_b2, old_b3, -- new_b4, new_b5, new_b6 --, old_b6, old_b7 )]
       */
      let startIndex = -1,
        endIndex = -1;
      updatedProactiveAIBlock.forEach((block, index) => {
        if (block.inlineNodeStart <= step.from && block.inlineNodeEnd >= step.from || block.inlineNodeStart <= step.to && block.inlineNodeEnd >= step.to) {
          if (startIndex === -1) {
            startIndex = index;
            endIndex = index;
          } else if (startIndex > -1) {
            endIndex = index;
          }
        }

        // or if block is within step range
        if (block.inlineNodeStart >= step.from && block.inlineNodeEnd <= step.to) {
          if (startIndex === -1) {
            startIndex = index;
            endIndex = index;
          } else if (startIndex > -1) {
            endIndex = index;
          }
        }

        // if range is between current and next block,
        //	thus neigher current or next block should be replaced
        if (index < updatedProactiveAIBlock.length - 1) {
          const nextBlock = updatedProactiveAIBlock[index + 1];
          if (step.from > block.inlineNodeEnd && step.to < nextBlock.inlineNodeStart) {
            /**
             * We want to add new block after current block.
             * So startIndex has to be "index + 1".
             * Also we don't want to delete any block.
             * So expression "endIndex - startIndex + 1" must be 0.
             * So setting endIndex to "index".
             */
            startIndex = index + 1;
            endIndex = index;
          }
        }

        // if step range is outside last block then it's new blocks
        if (index === updatedProactiveAIBlock.length - 1 && step.from > block.inlineNodeEnd && step.to > block.inlineNodeEnd) {
          startIndex = index + 1;
          endIndex = index + 1;
        }
      });
      if (startIndex > -1 && endIndex > -1) {
        updatedProactiveAIBlock.splice(startIndex, endIndex - startIndex + 1, ...newProactiveAIBlocks);
      }
    });
    updatedProactiveAIBlock = updatedProactiveAIBlock.map(block => {
      if (block.newBlock) {
        delete block.newBlock;
        delete block.node;
      } else {
        block.from = tr.mapping.map(block.from);
        block.to = tr.mapping.map(block.to);
        block.inlineNodeStart = tr.mapping.map(block.inlineNodeStart);
        block.inlineNodeEnd = tr.mapping.map(block.inlineNodeEnd);
      }
      return block;
    });
    return {
      originProactiveAIBlock: proactiveAIBlocks,
      updatedProactiveAIBlock
    };
  }
  return {
    originProactiveAIBlock: proactiveAIBlocks,
    updatedProactiveAIBlock: proactiveAIBlocks
  };
}
export function getAllProactiveAIBlocks(state) {
  const nodeTypes = getProactiveAIBlockNodeTypes(state);
  const includedNodeTypes = getIncludedProactiveAINodeTypes(state);
  const proactiveAIBlocks = [];
  state.doc.descendants((node, pos) => {
    if (nodeTypes.has(node.type)) {
      const inlineNodePositions = [];
      node.descendants((node, pos) => {
        if (node.isInline && !node.isText) {
          inlineNodePositions.push(pos);
        }
      });
      proactiveAIBlocks.push({
        id: uuid(),
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
        nodeTypeName: node.type.name,
        inlineNodeStart: pos + 1,
        inlineNodeEnd: pos + node.nodeSize - 1,
        inlineNodePositions,
        needSpellingAndGrammarCheck: false
      });
    }
    return includedNodeTypes.has(node.type);
  });
  return proactiveAIBlocks;
}